import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
    ChevronRight,
    CloseIcon,
    DownloadIcon,
    FilterIcon,
    HomeIcon,
    SearchIcon,
} from "./Icons";

/* ---------------------------------------------------------- breadcrumb --- */

export interface Crumb {
    label: string;
    to?: string;
}

export function Breadcrumb({ trail }: { trail: Crumb[] }) {
    return (
        <nav className="crumb" aria-label="Breadcrumb">
            <HomeIcon />
            {trail.map((c, i) => (
                <span key={`${c.label}-${i}`} className="crumb-item">
                    {i > 0 ? <ChevronRight size={13} /> : null}
                    {c.to && i < trail.length - 1 ? (
                        <Link to={c.to}>{c.label}</Link>
                    ) : (
                        <span aria-current={i === trail.length - 1 ? "page" : undefined}>
                            {c.label}
                        </span>
                    )}
                </span>
            ))}
        </nav>
    );
}

/* --------------------------------------------------- segmented control --- */

export interface Segment<T extends string> {
    key: T;
    label: string;
}

/**
 * A joined pill group for mutually exclusive ranges.
 *
 * Rendered as radios rather than buttons: it is a single-choice control, so
 * arrow keys should move between the options and a screen reader should
 * announce "3 of 5", which a row of buttons does not do.
 */
export function SegmentedControl<T extends string>({
    segments,
    value,
    onChange,
    label,
}: {
    segments: Segment<T>[];
    value: T;
    onChange: (v: T) => void;
    label: string;
}) {
    return (
        <div className="segmented" role="radiogroup" aria-label={label}>
            {segments.map((s) => (
                <button
                    key={s.key}
                    type="button"
                    role="radio"
                    aria-checked={value === s.key}
                    className="segment"
                    onClick={() => onChange(s.key)}
                >
                    {s.label}
                </button>
            ))}
        </div>
    );
}

/* ------------------------------------------------------------- toolbar --- */

export function Toolbar({
    title,
    subtitle,
    /** Opens the filter panel. Omit on views with nothing to filter. */
    onToggleFilters,
    filtersOpen,
    activeFilterCount = 0,
    /** Called with no arguments; the page owns what it exports. */
    onExport,
    exportDisabled,
    search,
    onSearch,
    searchPlaceholder = "Search…",
    right,
}: {
    title: string;
    subtitle?: ReactNode;
    onToggleFilters?: () => void;
    filtersOpen?: boolean;
    activeFilterCount?: number;
    onExport?: () => void;
    exportDisabled?: boolean;
    search?: string;
    onSearch?: (v: string) => void;
    searchPlaceholder?: string;
    right?: ReactNode;
}) {
    return (
        <div className="toolbar">
            <div className="toolbar-titles">
                <h2 className="toolbar-title">{title}</h2>
                {subtitle ? <div className="toolbar-sub">{subtitle}</div> : null}
            </div>

            <div className="toolbar-actions">
                {right}

                {onToggleFilters ? (
                    <button
                        type="button"
                        className={filtersOpen ? "btn-primary" : ""}
                        onClick={onToggleFilters}
                        aria-expanded={filtersOpen}
                    >
                        <FilterIcon />
                        Add Filter
                        {activeFilterCount > 0 ? (
                            <span className="count-badge">{activeFilterCount}</span>
                        ) : null}
                    </button>
                ) : null}

                {onExport ? (
                    <button
                        type="button"
                        onClick={onExport}
                        disabled={exportDisabled}
                        title={
                            exportDisabled
                                ? "Nothing to export in the current view"
                                : "Download the rows currently shown as CSV"
                        }
                    >
                        <DownloadIcon />
                        Export
                    </button>
                ) : null}

                {onSearch ? (
                    <span className="search-wrap">
                        <span className="search-icon"><SearchIcon /></span>
                        <input
                            type="search"
                            className="search-input"
                            placeholder={searchPlaceholder}
                            value={search ?? ""}
                            onChange={(e) => onSearch(e.target.value)}
                            aria-label={searchPlaceholder}
                        />
                        {search ? (
                            <button
                                type="button"
                                className="search-clear"
                                onClick={() => onSearch("")}
                                aria-label="Clear search"
                            >
                                <CloseIcon size={12} />
                            </button>
                        ) : null}
                    </span>
                ) : null}
            </div>
        </div>
    );
}

/**
 * The two date inputs for a custom window.
 *
 * Shown inline next to the segmented control, only while "Custom" is selected.
 * It is not behind "Add Filter": on a view whose only filter IS the range,
 * that button opened a panel containing nothing but a sentence telling the
 * reader to use the control above it.
 */
export function CustomRange({
    from,
    to,
    onFrom,
    onTo,
    idPrefix,
}: {
    from: string;
    to: string;
    onFrom: (v: string) => void;
    onTo: (v: string) => void;
    /** Keeps the label/input ids unique when two ranges share a page. */
    idPrefix: string;
}) {
    return (
        <span className="date-range">
            <label className="muted" htmlFor={`${idPrefix}-from`}>From</label>
            <input
                id={`${idPrefix}-from`}
                type="date"
                value={from}
                max={to || undefined}
                onChange={(e) => onFrom(e.target.value)}
            />
            <label className="muted" htmlFor={`${idPrefix}-to`}>To</label>
            <input
                id={`${idPrefix}-to`}
                type="date"
                value={to}
                min={from || undefined}
                onChange={(e) => onTo(e.target.value)}
            />
        </span>
    );
}

/** The collapsible row of real filter controls the toolbar's button reveals. */
export function FilterPanel({
    children,
    onClear,
    activeCount,
}: {
    children: ReactNode;
    onClear?: () => void;
    activeCount: number;
}) {
    return (
        <div className="filter-panel">
            <div className="controls">{children}</div>
            {activeCount > 0 && onClear ? (
                <button type="button" className="filter-clear" onClick={onClear}>
                    Clear {activeCount} filter{activeCount === 1 ? "" : "s"}
                </button>
            ) : null}
        </div>
    );
}
